import { Link, useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CategoryListIcon from "../../components/CustomComponents/CategoryListIcon/CategoryListIcon";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";

const RenderCategoryRow = ({
  category,
  index,
  level = 0,
  parentIndex = null,
  grandParentIndex = null,
  categories,
  setCategories,
  visibleColumns,
  DeleteCategory,
}) => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [isExpanded, setIsExpanded] = useState(false);
  const [statusConfirmCategory, setStatusConfirmCategory] = useState(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const navigate = useNavigate();

  const hasSubcategories =
    category.subCategoriesList && category.subCategoriesList.length > 0;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCheckboxChange = (checked) => {
    const updateCheckedStatus = (categories, targetId, newCheckedState) => {
      return categories.map((cat) => {
        if (cat.rootCat_ID === targetId) {
          const updatedCategory = { ...cat, checked: newCheckedState };
          if (
            updatedCategory.subCategoriesList &&
            updatedCategory.subCategoriesList.length > 0
          ) {
            updatedCategory.subCategoriesList = updateCheckedStatus(
              updatedCategory.subCategoriesList,
              targetId,
              newCheckedState
            );
          }
          return updatedCategory;
        }
        if (cat.subCategoriesList && cat.subCategoriesList.length > 0) {
          return {
            ...cat,
            subCategoriesList: updateCheckedStatus(
              cat.subCategoriesList,
              targetId,
              newCheckedState
            ),
          };
        }
        return cat;
      });
    };
    setCategories((prev) =>
      updateCheckedStatus(prev, category.rootCat_ID, checked)
    );
  };

  const updateCategoryPublishInTree = (cats, targetId, newPublish) => {
    return cats.map((cat) => {
      if (cat.rootCat_ID === targetId) {
        return { ...cat, rootCat_Publish: newPublish };
      }
      if (cat.subCategoriesList?.length > 0) {
        return {
          ...cat,
          subCategoriesList: updateCategoryPublishInTree(
            cat.subCategoriesList,
            targetId,
            newPublish
          ),
        };
      }
      return cat;
    });
  };

  const confirmCategoryStatusChange = async () => {
    if (!statusConfirmCategory) return;

    const { rootCat_ID, rootCat_Publish, rootCat_Name } = statusConfirmCategory;
    const newStatus = !rootCat_Publish;

    setCategories((prev) =>
      updateCategoryPublishInTree(prev, rootCat_ID, newStatus)
    );
    setStatusConfirmCategory(null);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_CATEGORY_STATUS, {
        CompID: CompID,
        RootCat_ID: rootCat_ID,
        RootCat_Publish: newStatus,
      });
      showSuccessToast(
        `"${rootCat_Name}" is now ${newStatus ? "Active" : "InActive"}.`
      );
    } catch (error) {
      setCategories((prev) =>
        updateCategoryPublishInTree(prev, rootCat_ID, rootCat_Publish)
      );
      showErrorToast("Failed to update category status.");
    }
  };

  const handleDragStart = (e) => {
    dragItem.current = { index, parentIndex, grandParentIndex };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify(dragItem.current)
    );
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragOverItem.current = { index, parentIndex, grandParentIndex };
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));
    if (!dragData || !dragOverItem.current) return;

    const {
      index: draggedIndex,
      parentIndex: draggedParentIndex,
      grandParentIndex: draggedGrandParentIndex,
    } = dragData;

    const {
      index: overIndex,
      parentIndex: overParentIndex,
      grandParentIndex: overGrandParentIndex,
    } = dragOverItem.current;

    if (
      draggedIndex === overIndex &&
      draggedParentIndex === overParentIndex &&
      draggedGrandParentIndex === overGrandParentIndex
    ) {
      resetDragState();
      return;
    }

    if (
      draggedParentIndex !== overParentIndex ||
      draggedGrandParentIndex !== overGrandParentIndex
    ) {

      resetDragState();
      return;
    }

    let itemsToReorder;
    if (parentIndex === null && grandParentIndex === null) {
      itemsToReorder = [...categories];
    } else if (grandParentIndex === null) {
      itemsToReorder = [...categories[parentIndex].subCategoriesList];
    } else {
      itemsToReorder = [
        ...categories[grandParentIndex].subCategoriesList[parentIndex]
          .subCategoriesList,
      ];
    }

    const [draggedItem] = itemsToReorder.splice(draggedIndex, 1);
    itemsToReorder.splice(overIndex, 0, draggedItem);

    const updatedItems = itemsToReorder.map((item, index) => ({
      ...item,
      rootCat_Orderby: index + 1,
    }));

    setCategories((prevCategories) => {
      const newCategories = [...prevCategories];

      if (parentIndex === null && grandParentIndex === null) {
        return updatedItems;
      } else if (grandParentIndex === null) {
        newCategories[parentIndex].subCategoriesList = updatedItems;
      } else {
        newCategories[grandParentIndex].subCategoriesList[
          parentIndex
        ].subCategoriesList = updatedItems;
      }

      return newCategories;
    });

    try {
      const sortUpdates = updatedItems.map((item) => ({
        rootCat_ID: item.rootCat_ID,
        rootCat_Orderby: item.rootCat_Orderby,
      }));

      await axiosInstance.post(
        APIs.POST_UPDATE_CATEGORY_SORT_ORDER,
        {
          CompID: CompID,
          Updates: sortUpdates,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {

      setCategories(categories);
    }

    resetDragState();
  };

  const resetDragState = () => {
    dragItem.current = null;
    dragOverItem.current = null;
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.opacity = "1";
      tr.style.backgroundColor = "";
    });
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });

    if (dragItem.current) {
      dragItem.current = null;
    }
    if (dragOverItem.current) {
      dragOverItem.current = null;
    }
  };

  const handleDeleteCategory = () => {
    DeleteCategory(category, level > 0);
  };

  return (
    <>
      <tr
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        onClick={(e) => {
          if (
            e.target.closest(".status-badge") ||
            e.target.closest(".dropdown") || // Action dropdown
            e.target.closest(".fa-bars") || // Drag handle
            e.target.closest(".fa-chevron-down") || // Expand/collapse icon
            e.target.closest(".fa-chevron-right") || // Expand/collapse icon
            e.target.closest("input[type='checkbox']") || // Checkbox
            e.target.tagName === "A" // Links in dropdown
          ) {
            return;
          } else {
            navigate(`/categories/updateCategory/${category.rootCat_ID}`);
          }
        }}
        style={{ cursor: "pointer" }} // Indicate the row is clickable
        className={`${parentIndex !== null ? "bg-light bg-opacity-50" : ""}`}
      >
        <td
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="align-middle"
          style={{ width: "50px" }}
        >
          <div className="d-flex align-items-center">
            <input
              type="checkbox"
              style={{ marginRight: "8px" }}
              checked={category.checked || false}
              onChange={(e) => handleCheckboxChange(e.target.checked)}
            />
            <i
              className="fa fa-bars text-secondary"
              style={{ cursor: "grab", marginRight: "10px" }}
            ></i>
          </div>
        </td>

        <td

          className="align-middle"
        >
          <div className="d-flex align-items-center">
            {Array(level)
              .fill()
              .map((_, i) => (
                <div key={i} style={{ width: "20px" }}></div>
              ))}
            {hasSubcategories && (
              <div onClick={(e) => {
                e.stopPropagation();
                toggleExpand();
              }}
                className="me-2"
                style={{ cursor: "pointer", width: "20px" }}

              >
                <i
                  className={`fa ${isExpanded ? "fa-chevron-down" : "fa-chevron-right"
                    }`}
                ></i>
              </div>
            )}
            {!hasSubcategories && parentIndex !== null && (
              <div className="me-2" style={{ width: "20px" }}></div>
            )}
            <CategoryListIcon
              src={category.rootCat_Image}
              alt={category.rootCat_Name}
              className="me-2"
            />
            <span>{category.rootCat_Name}</span>
          </div>
        </td>

        {visibleColumns.department && (
          <td
            className="align-middle text-start"
            onClick={() =>
              navigate(`/categories/updateCategory/${category.rootCat_ID}`)
            }
          >
            {category.departmentName ?? category.rootCat_DepartmentName ?? (category.categoryDepartments?.[0]?.departmentName ?? category.categoryDepartments?.[0]?.name) ?? "—"}
          </td>
        )}
        {visibleColumns.categoryType && (
          <td
            className="align-middle text-start"
            onClick={() =>
              navigate(`/categories/updateCategory/${category.rootCat_ID}`)
            }
          >
            {category.reportingCategoryName ?? category.categoryTypeName ?? category.rootCat_TypeName ?? (category.rootCat_Type !== undefined && category.rootCat_Type !== null ? String(category.rootCat_Type) : "—")}
          </td>
        )}
        {visibleColumns.products && (
          <td className="align-middle text-center">{category.products}</td>
        )}
        {visibleColumns.status && (
          <td
            className="align-middle text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <StatusBadge
              isActive={category.rootCat_Publish}
              onClick={() => setStatusConfirmCategory(category)}
            />
          </td>
        )}

        <td className="align-middle text-start">
          <TableActionsMenu>
            <TableActionsItem
              as={Link}
              to={`/categories/updateCategory/${category.rootCat_ID}`}
              icon="fa fa-edit"
            >
              Edit
            </TableActionsItem>
            <TableActionsItem
              as={Link}
              to={`/items/manageItems/${category.rootCat_ID}`}
              icon="fa fa-box"
            >
              Products
            </TableActionsItem>
            <TableActionsItem
              danger
              icon="fa fa-trash"
              onClick={handleDeleteCategory}
            >
              Delete
            </TableActionsItem>
            {hasSubcategories && (
              <TableActionsItem
                icon={isExpanded ? "fa fa-chevron-up" : "fa fa-chevron-down"}
                onClick={toggleExpand}
              >
                {isExpanded ? "Collapse" : "Expand"}
              </TableActionsItem>
            )}
            <TableActionsItem
              as={Link}
              to={`/categories/addCategory/${category.rootCat_ID}`}
              icon="fa fa-plus"
            >
              Add Subcategory
            </TableActionsItem>
          </TableActionsMenu>
        </td>
      </tr>

      {hasSubcategories &&
        isExpanded &&
        category.subCategoriesList.map((subcategory, subIndex) => (
          <RenderCategoryRow
            key={subcategory.rootCat_ID}
            category={subcategory}
            index={subIndex}
            level={level + 1}
            parentIndex={index}
            grandParentIndex={parentIndex}
            categories={categories}
            setCategories={setCategories}
            visibleColumns={visibleColumns}
            DeleteCategory={DeleteCategory}
          />
        ))}

      <CustomModal
        title="Change category status?"
        subtitle={
          statusConfirmCategory
            ? `Are you sure you want to set "${statusConfirmCategory.rootCat_Name}" to ${
                statusConfirmCategory.rootCat_Publish ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmCategory(null)}
        isOpen={!!statusConfirmCategory}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmCategoryStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </>
  );
};

export default RenderCategoryRow;
